/**
 * /privacy — public privacy policy.
 *
 * Required by Apple + Google for store submission, by RevenueCat for
 * webhook approval, and by GDPR / UK-GDPR / CCPA for users in those
 * jurisdictions. Linked from /account/delete, the signup screens, and
 * every store listing.
 *
 * The content here reflects WYDM's *actual* data practices — what the
 * backend collects, the third parties it sends data to, the retention
 * policy implemented in the account-deletion path. Update this file
 * whenever data practices change; it shouldn't drift from the code.
 *
 * Template-shaped + reviewed against UK ICO guidance, but this is
 * NOT legal advice. Get a solicitor to review before launch in any
 * regulated market.
 */
import { Link } from 'react-router-dom';

const LAST_UPDATED = '22 May 2026';

export function PrivacyPage() {
  return (
    <main style={page}>
      <div style={banner}>
        <strong>Pre-launch draft.</strong> This policy reflects WYDM's actual data
        practices but has not been reviewed by legal counsel. Review with a
        solicitor before public launch.
      </div>

      <h1 className="serif" style={h1}>Privacy Policy</h1>
      <p style={meta}>Last updated: {LAST_UPDATED}</p>

      <p style={para}>
        This policy explains what data WYDM collects, why we collect it, who
        we share it with, how long we keep it, and the rights you have over it.
        WYDM is operated by Callo Apps Ltd, United Kingdom. We're the data
        controller for any personal data this app processes.
      </p>

      <h2 className="serif" style={h2}>Quick summary</h2>
      <ul style={ul}>
        <li>We collect the minimum needed to run a dating-feedback service: account email, profile content, photos, ratings of and from your profile, payment metadata.</li>
        <li>Ratings of your profile by others are <strong>anonymous to you</strong>. We don't tell you who rated you.</li>
        <li>We use a small set of third-party processors (RevenueCat, Anthropic, Cloudflare, Resend, hCaptcha, Spotify if you connect it). We don't sell or share data for advertising.</li>
        <li>You can delete your account in-app at any time. After a 30-day grace window, your data is permanently removed. See <Link to="/account/delete" style={inlineLink}>wydm.app/account/delete</Link>.</li>
        <li>You can request an export of your data from in-app settings. We deliver it within 30 days.</li>
        <li>You must be 18 or older to use WYDM.</li>
      </ul>

      <h2 className="serif" style={h2}>1. What we collect</h2>

      <h3 style={h3}>Account information</h3>
      <ul style={ul}>
        <li>Email address (always required)</li>
        <li>Password (hashed with argon2id; we cannot read it)</li>
        <li>If you sign in with Apple or Google: the OAuth provider's stable user id, your verified email, and your display name if shared by the provider</li>
        <li>Date of birth (used for the 18+ gate; never shown on your profile)</li>
        <li>Display name (shown to other users)</li>
      </ul>

      <h3 style={h3}>Profile content</h3>
      <ul style={ul}>
        <li>Bio text (up to 500 characters)</li>
        <li>Gender and "looking for" selections</li>
        <li>Photos you upload</li>
        <li>If you connect Spotify: your top 3 tracks and top 3 artists from the last 4 weeks, refreshed daily</li>
      </ul>

      <h3 style={h3}>Activity data</h3>
      <ul style={ul}>
        <li>Ratings you receive (verdict + optional 240-char note, anonymous to you)</li>
        <li>Ratings you give (if you're signed in when you rate — linked to your account only so we can determine DM eligibility; never disclosed to the profile owner)</li>
        <li>AI reviews you generate (subject + body, kept until you delete them or your account)</li>
        <li>Direct messages you send and receive</li>
        <li>Notification preferences</li>
        <li>Device tokens for push notifications (Expo / FCM / APNs / Web Push)</li>
      </ul>

      <h3 style={h3}>Payment information</h3>
      <ul style={ul}>
        <li>We do not see or store your card / payment account number.</li>
        <li>RevenueCat → Apple/Google hold the actual payment instrument. Subscriptions are mobile-only; we don't process card payments on the web.</li>
        <li>We store: vendor identifiers (customer id, subscription id), price id, billing period, status, dates. Required for tax and chargeback records.</li>
      </ul>

      <h3 style={h3}>Technical data</h3>
      <ul style={ul}>
        <li>IP address (logged for rate-limiting and abuse detection; not shown to other users)</li>
        <li>User-agent header (logged for the same reasons)</li>
        <li>Anonymous browser fingerprint when rating profiles (LocalStorage; used to deduplicate ratings; we cannot link this to a specific person)</li>
        <li>Server access logs (15-day retention)</li>
      </ul>

      <h2 className="serif" style={h2}>2. Why we collect it (UK-GDPR lawful bases)</h2>
      <ul style={ul}>
        <li><strong>Contract (Art. 6(1)(b)):</strong> account creation, profile hosting, payment processing, sending you messages and notifications, AI reviews, DMs.</li>
        <li><strong>Legitimate interest (Art. 6(1)(f)):</strong> abuse prevention (rate-limiting, CSAM scanning, NSFW filtering, content moderation), security logging, fraud detection, product improvement.</li>
        <li><strong>Legal obligation (Art. 6(1)(c)):</strong> tax records, responding to law-enforcement requests, child-safety reporting where required.</li>
        <li><strong>Consent (Art. 6(1)(a)):</strong> connecting your Spotify account, opting in to product-update emails.</li>
      </ul>

      <h2 className="serif" style={h2}>3. Who we share it with</h2>
      <p style={para}>
        We use these third-party processors. Each has signed a data-processing
        agreement and processes data only on our instructions.
      </p>
      <ul style={ul}>
        <li><strong>Cloudflare</strong> (R2 object storage for photos; CSAM scanning of public images). United States; UK-EU Data Transfer Risk Assessment in place.</li>
        <li><strong>RevenueCat</strong> (in-app purchase orchestration on iOS + Android). United States.</li>
        <li><strong>Apple</strong> (Sign in with Apple, App Store IAP). Ireland (EU) / United States.</li>
        <li><strong>Google</strong> (Sign in with Google, Google Play Billing). Ireland (EU) / United States.</li>
        <li><strong>Anthropic</strong> (AI review generation; only your profile snapshot is sent, never DMs). United States.</li>
        <li><strong>Resend</strong> (transactional email delivery). United States.</li>
        <li><strong>Expo / FCM / APNs</strong> (push notification fan-out). United States.</li>
        <li><strong>hCaptcha</strong> (abuse prevention on the public rate endpoint). United States; processes IP + browser data only.</li>
        <li><strong>Spotify</strong> (only if you connect it; we receive your top 3 tracks + artists). Sweden / United States.</li>
        <li><strong>Hetzner</strong> (server hosting + Postgres database + self-hosted log storage). Germany (EU).</li>
      </ul>
      <p style={para}>
        We do <strong>not</strong> sell or rent personal data, and we do not
        share data with advertising networks. If we ever do, we'll ask for
        consent first and update this page.
      </p>

      <h2 className="serif" style={h2}>4. How long we keep it</h2>
      <ul style={ul}>
        <li><strong>Account + profile + photos + DMs + ratings + AI reviews:</strong> until you delete your account, then a 30-day grace window, then permanently removed (within an additional 90 days from our backups).</li>
        <li><strong>Payment records</strong> (vendor IDs, amounts, dates, outcomes): 7 years (US tax) or 10 years (EU VAT). The personal link to you is unset after account deletion; only the financial trail survives.</li>
        <li><strong>Server access logs:</strong> 15 days.</li>
        <li><strong>Security audit log</strong> (sign-in attempts, password resets, admin actions): 2 years from event date.</li>
        <li><strong>Webhook event log</strong> (payment provider events): 7 years for fraud + reconciliation.</li>
      </ul>

      <h2 className="serif" style={h2}>5. Your rights</h2>
      <p style={para}>
        Under UK-GDPR (and equivalent rights under EU-GDPR, CCPA, and similar
        laws), you have the right to:
      </p>
      <ul style={ul}>
        <li><strong>Access</strong> the personal data we hold about you — use the data export button in Settings, or email <a href="mailto:support@wydm.app" style={inlineLink}>support@wydm.app</a></li>
        <li><strong>Correct</strong> data that's wrong — edit your profile in-app, or email us</li>
        <li><strong>Delete</strong> your account and data — Settings → Danger zone → Delete account, or follow the steps at <Link to="/account/delete" style={inlineLink}>wydm.app/account/delete</Link></li>
        <li><strong>Object</strong> to specific processing (e.g. notifications) — toggle in Settings</li>
        <li><strong>Withdraw consent</strong> for processing where consent is the basis (e.g. disconnect Spotify) — Settings</li>
        <li><strong>Portability</strong> — receive your data in a machine-readable JSON format via the export</li>
        <li><strong>Lodge a complaint</strong> with the UK Information Commissioner's Office (<a href="https://ico.org.uk/make-a-complaint/" style={inlineLink} target="_blank" rel="noopener">ico.org.uk</a>) or your country's data protection authority</li>
      </ul>
      <p style={para}>
        We respond to verifiable requests within 30 days. There is no fee for
        the first request in a 12-month period.
      </p>

      <h2 className="serif" style={h2}>6. Cookies and local storage</h2>
      <ul style={ul}>
        <li>We do not use tracking cookies or advertising pixels.</li>
        <li>The web app uses LocalStorage to keep your sign-in tokens and a per-browser rating fingerprint. Both are first-party only and never sent to advertisers.</li>
        <li>If you sign in via OAuth, the provider may set its own cookies during the flow — those are governed by Apple's / Google's privacy policies.</li>
      </ul>

      <h2 className="serif" style={h2}>7. Children</h2>
      <p style={para}>
        WYDM is for users 18 and older. We collect a date of birth at signup
        and refuse accounts for under-18s. If you believe a child under 18 has
        registered, email us at <a href="mailto:support@wydm.app" style={inlineLink}>support@wydm.app</a> and we'll remove the account.
      </p>

      <h2 className="serif" style={h2}>8. International transfers</h2>
      <p style={para}>
        Some processors named in section 3 are based outside the UK / EEA. We
        rely on the UK International Data Transfer Agreement (IDTA) or the
        EU Standard Contractual Clauses (SCCs) with each, and we apply
        additional safeguards (encryption in transit and at rest, contractual
        purpose limitation) consistent with the ICO's Transfer Risk Assessment
        guidance.
      </p>

      <h2 className="serif" style={h2}>9. Security</h2>
      <p style={para}>
        We protect data in transit with TLS, store passwords as argon2id
        hashes, encrypt secrets at rest with pgcrypto, restrict admin access
        with two-factor TOTP, scan all uploaded images for CSAM via Cloudflare's
        free scanner, and run NSFW detection before any image is publicly visible.
      </p>

      <h2 className="serif" style={h2}>10. Changes to this policy</h2>
      <p style={para}>
        If we make material changes — new processors, new data categories,
        new uses — we'll update this page and notify you via email or
        in-app notification before the changes take effect.
      </p>

      <h2 className="serif" style={h2}>11. Contact</h2>
      <p style={para}>
        Data controller: Callo Apps Ltd, United Kingdom.<br />
        Email: <a href="mailto:support@wydm.app" style={inlineLink}>support@wydm.app</a><br />
        Postal address available on request.
      </p>

      <div style={footer}>
        <Link to="/terms" style={inlineLink}>Terms of Service</Link>{' · '}
        <Link to="/account/delete" style={inlineLink}>Delete your account</Link>{' · '}
        <Link to="/" style={inlineLink}>Home</Link>
      </div>
    </main>
  );
}

// ── styles (shared with /terms via component duplication; small enough
// to inline). ──────────────────────────────────────────────────────────

const page: React.CSSProperties = { maxWidth: 760, margin: '0 auto', padding: '64px 24px' };
const banner: React.CSSProperties = {
  marginTop: 24, padding: '14px 18px',
  background: 'rgba(232,106,76,0.06)', border: '1px solid rgba(232,106,76,0.2)', borderRadius: 12,
  fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5,
};
const h1: React.CSSProperties = { fontSize: 44, fontWeight: 500, lineHeight: 1.1, marginTop: 32, marginBottom: 8, letterSpacing: -0.5 };
const meta: React.CSSProperties = { fontSize: 13, color: 'var(--ink-3)', marginBottom: 36, fontFamily: 'ui-monospace, monospace' };
const h2: React.CSSProperties = { fontSize: 24, fontWeight: 500, fontStyle: 'italic', marginTop: 40, marginBottom: 14 };
const h3: React.CSSProperties = { fontSize: 16, fontWeight: 600, marginTop: 18, marginBottom: 8 };
const para: React.CSSProperties = { fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.65, marginBottom: 14 };
const ul: React.CSSProperties = { paddingLeft: 22, margin: '0 0 14px 0', fontSize: 15, lineHeight: 1.7, color: 'var(--ink)' };
const inlineLink: React.CSSProperties = { color: 'var(--coral)', textDecoration: 'underline', fontWeight: 600 };
const footer: React.CSSProperties = { marginTop: 48, paddingTop: 24, borderTop: '1px solid var(--line)', fontSize: 13, color: 'var(--ink-3)' };
